import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { IMPORT_CONSIGNMENT_TYPES, IMPORT_PERMIT_STATUSES, type ImportConsignmentType, type ImportHealthPermit, type ImportPermitStatus, type Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/** Import-permit referee duty (48h SLA) — packages/modules/m25-public-health/src/routes/importPermits.ts. Real M09 (Customs) single-window integration is out of scope this wave; PORT_HEALTH_OFFICER/SENIOR_PHI log referrals arriving by the current interim channel. */
export function ImportPermits() {
  const { persona } = useAuth();
  const canAct = (persona?.roles.includes('PORT_HEALTH_OFFICER') || persona?.roles.includes('SENIOR_PHI')) ?? false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<ImportHealthPermit>>(`/api/v1/m25/import-permits?${qs.toString()}`);

  return (
    <div>
      <PageHeader
        title="Import Health Permits"
        subtitle="Food / pharma / cosmetics consignment referee duty — 48-hour SLA from creation."
        actions={
          canAct ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + New referral
            </button>
          ) : undefined
        }
      />

      {!canAct ? (
        <div className="mb-4">
          <ErrorNote message="Viewing/responding to import permit referrals requires PORT_HEALTH_OFFICER or SENIOR_PHI." />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <input className="field-input !w-48" placeholder="Search ref no. / importer…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field-input !w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {IMPORT_PERMIT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No import permit referrals match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref no.</th>
                <th className="table-head">Type</th>
                <th className="table-head">Importer</th>
                <th className="table-head">Status</th>
                <th className="table-head">Due by</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => {
                const overdue = p.status === 'PENDING' && new Date(p.dueBy).getTime() < Date.now();
                return (
                  <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(p.id)}>
                    <td className="table-cell font-mono text-xs">{p.refNo}</td>
                    <td className="table-cell">
                      <StatusBadge status={p.consignmentType} />
                    </td>
                    <td className="table-cell">{p.importerId}</td>
                    <td className="table-cell">
                      <StatusBadge status={p.status} />
                      {p.slaBreached ? <span className="ml-1 text-xs text-rose-600">SLA breached</span> : null}
                    </td>
                    <td className={`table-cell text-xs ${overdue ? 'font-semibold text-rose-600' : ''}`}>{formatDateTime(p.dueBy)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {detailId ? <DetailModal id={detailId} canAct={canAct} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} /> : null}
    </div>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [consignmentType, setConsignmentType] = useState<ImportConsignmentType>('FOOD');
  const [importerType, setImporterType] = useState<'PERSON' | 'ENTITY'>('ENTITY');
  const [importerId, setImporterId] = useState('');
  const [description, setDescription] = useState('');
  const [originCountry, setOriginCountry] = useState('');
  const [quantity, setQuantity] = useState('');
  const [consignmentRef, setConsignmentRef] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m25/import-permits', {
        consignmentType,
        importerType,
        importerId,
        description,
        originCountry: originCountry || undefined,
        quantity: quantity || undefined,
        consignmentRef: consignmentRef || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to log referral');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New import permit referral" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Consignment type">
            <select className="field-input" value={consignmentType} onChange={(e) => setConsignmentType(e.target.value as ImportConsignmentType)}>
              {IMPORT_CONSIGNMENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Importer type">
            <select className="field-input" value={importerType} onChange={(e) => setImporterType(e.target.value as 'PERSON' | 'ENTITY')}>
              <option value="ENTITY">Entity</option>
              <option value="PERSON">Person</option>
            </select>
          </Field>
        </div>
        <Field label="Importer id (business id or NIN)">
          <input className="field-input" value={importerId} onChange={(e) => setImporterId(e.target.value)} />
        </Field>
        <Field label="Description">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Frozen poultry, 2 x 20ft containers" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Origin country (optional)">
            <input className="field-input" value={originCountry} onChange={(e) => setOriginCountry(e.target.value)} />
          </Field>
          <Field label="Quantity (optional)">
            <input className="field-input" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </Field>
        </div>
        <Field label="Consignment / manifest reference (optional)">
          <input className="field-input" value={consignmentRef} onChange={(e) => setConsignmentRef(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || importerId.trim().length < 3 || description.trim().length < 3}>
            {busy ? 'Logging…' : 'Log referral'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DetailModal({ id, canAct, onClose, onChanged }: { id: string; canAct: boolean; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<ImportHealthPermit>(`/api/v1/m25/import-permits/${id}`);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const permit = detailQ.data;

  const respond = async (decision: ImportPermitStatus) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m25/import-permits/${id}/respond`, { decision, notes: notes || undefined });
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not respond');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Import health permit referral" onClose={onClose}>
      {detailQ.loading || !permit ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center justify-between">
            <StatusBadge status={permit.consignmentType} />
            <StatusBadge status={permit.status} />
          </div>
          <dl className="space-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Importer</dt>
              <dd className="font-medium">
                {permit.importerId} ({permit.importerType})
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Description</dt>
              <dd className="text-right font-medium">{permit.description}</dd>
            </div>
            {permit.originCountry ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">Origin</dt>
                <dd className="font-medium">{permit.originCountry}</dd>
              </div>
            ) : null}
            <div className="flex justify-between">
              <dt className="text-slate-500">SLA due by</dt>
              <dd className={`font-medium ${permit.slaBreached ? 'text-rose-600' : ''}`}>{formatDateTime(permit.dueBy)}</dd>
            </div>
            {permit.decisionNotes ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">Notes</dt>
                <dd className="text-right font-medium">{permit.decisionNotes}</dd>
              </div>
            ) : null}
          </dl>

          {(permit.status === 'PENDING' || permit.status === 'HOLD') && canAct ? (
            <div className="space-y-2 border-t border-slate-100 pt-3">
              <Field label="Notes (optional)">
                <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>
              <div className="flex flex-wrap gap-2">
                <button className="btn-success" onClick={() => respond('APPROVED')} disabled={busy}>
                  Approve
                </button>
                <button className="btn-danger" onClick={() => respond('REJECTED')} disabled={busy}>
                  Reject
                </button>
                <button className="btn-secondary" onClick={() => respond('HOLD')} disabled={busy}>
                  Hold
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
